import { enrichItemWithOtherInfo, formatDate } from '@moko/utils';
import { useContext, useEffect, useRef, useState } from 'react';
import PagedList, {
  IListPageColumn,
  IPagedListRefType,
} from '../../common/PagedList';
import { getMetadata } from '../../services/metadataService';
import { getRenter } from '../../services/renterService';
import { getRent, getRents } from '../../services/rentService';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  clearMessageAtom,
  errorMessageAtom,
  isInProgressAtom,
} from '../../atoms/messageBarAtoms';
import { appRoles } from '../../authConfig';
import AppContext from '../AppContext';

const Rents = () => {
  const query = new URLSearchParams(useLocation().search);
  const renterId = Number.parseInt(query.get('id') ?? '');

  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();

  const [selectedRentId, setSelectedRentId] = useState<number>();

  const pagedListRef = useRef<IPagedListRefType>(null);

  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const clearMessage = useSetAtom(clearMessageAtom);
  const columns = useRef<IListPageColumn[]>([]);

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);

    try {
      const abortController = new AbortController();
      const renter = await getRenter(abortController, renterId);
      const metadata = await getMetadata(abortController, 'Rent');
      const otherInfoFields = metadata?.fields.filter((mf) => mf.showInGrid);
      otherInfoFields?.forEach((mf) => {
        if (!columns.current.find((c) => c.key === mf.name)) {
          columns.current.push({
            key: mf.name,
            name: mf.name,
            minWidth: 100,
            onRender:
              mf.fieldType === 'Image'
                ? (item: any) => (
                    <img src={item[mf.name]} style={{ width: 20 }} alt="" />
                  )
                : mf.fieldType === 'Date' || mf.fieldType === 'DateTime'
                ? (item: any) =>
                    item[mf.name]
                      ? formatDate(
                          String(item[mf.name]),
                          mf.fieldType === 'DateTime'
                        )
                      : ''
                : undefined,
          });
        }
      });
      clearMessage();
      context.setSelectedTab(`Rents of ${renter.name}`);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <PagedList
        selectedTab="Rents"
        ref={pagedListRef}
        listHeigh={525}
        columns={[
          {
            key: 'id',
            name: 'Id',
            minWidth: 25,
            maxWidth: 50,
            isResizable: true,
          },
          {
            key: 'code',
            name: 'Code',
            minWidth: 70,
            maxWidth: 100,
            isResizable: true,
          },
          {
            key: 'assetName',
            name: 'Asset',
            minWidth: 25,
            maxWidth: 50,
            isResizable: true,
          },
          {
            key: 'createdAt',
            name: 'Created at',
            minWidth: 125,
            maxWidth: 155,
            isResizable: true,
          },
          {
            key: 'fromDate',
            name: 'From',
            minWidth: 125,
            maxWidth: 155,
            isResizable: true,
          },
          {
            key: 'toDate',
            name: 'To',
            minWidth: 125,
            maxWidth: 155,
            isResizable: true,
          },
          ...columns.current,
        ]}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search
        ) => {
          const data = await getRents(
            abortController,
            pageSize,
            pageNo,
            renterId,
            orderBy,
            search
          );

          const fieldNames = columns.current
            .map((mf) => mf?.name)
            .filter(Boolean) as string[];
          data.items = data.items.map((rent) => {
            const newRent: any = {
              ...rent,
              createdAt: formatDate(rent.createdAt, true),
              fromDate: formatDate(rent.fromDate, true),
              toDate: formatDate(rent.toDate, true),
            };
            return enrichItemWithOtherInfo(newRent, fieldNames);
          });
          return data;
        }}
        commandBarItems={[
          {
            key: 'back',
            text: '',
            iconProps: { iconName: 'Back' },
            onClick: () => {
              navigate('/renters');
            },
          },
          {
            key: 'newRent',
            text: 'New',
            iconProps: { iconName: 'Add' },
            onClick: () => {
              navigate(`/newRent?renterId=${renterId}`);
            },
            disabled: !profileData.roles.includes(appRoles.Admin),
          },
          {
            key: 'editRent',
            text: 'Edit',
            iconProps: { iconName: 'Edit' },
            onClick: () => {
              navigate(`/editRent?renterId=${renterId}&id=${selectedRentId}`);
            },
            disabled:
              !profileData.roles.includes(appRoles.Admin) || !selectedRentId,
          },
        ]}
        onSelectionChange={(selection: any) => {
          setSelectedRentId(Number(selection.id));
        }}
        onRowDoubleClicked={(selection: any) => {
          navigate(`/editRent?renterId=${renterId}&id=${selection.id}`);
        }}
      ></PagedList>
    </>
  );
};

export default Rents;
